// src/App.js
import React, { useEffect, useMemo, useState } from "react";
import "./design-system.css";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Login from "./components/Login";
import Menu from "./components/Menu";
import ClientesPage from "./pages/ClientesPage";
import ProductosPage from "./pages/ProductosPage";
import CotizacionesPage from "./pages/CotizacionesPage";
import CotizacionForm from "./pages/CotizacionForm";
import CotizacionEditPage from "./pages/CotizacionEditPage";
import CarteraPage from "./pages/CarteraPage";
import FacturaDetallePage from "./components/FacturaDetallePage";
import FacturaPage40 from "./pages/FacturaPage40";
import FacturaFormPage from "./pages/FacturaFormPage";
import FacturaDetalle40 from "./components/FacturaDetalle40";
import FacturaPrint40 from "./components/FacturaPrint40";
import CotizacionPrint40 from "./components/CotizacionPrint40";
import PagosClientesPage from "./pages/PagosClientesPage";
import PagosListaPage from "./pages/PagosListaPage";
import CpPrintPage from "./pages/CpPrintPage";
import ProveedoresCatalogo from "./pages/ProveedoresCatalogo";
import ProveedoresFacturasPage from "./pages/ProveedoresFacturasPage";
import DashboardPage from "./pages/DashboardPage";
import UsuariosPage from "./pages/UsuariosPage";
import EmpresasPage from "./pages/EmpresasPage";
import ReporteFacturasPage from "./pages/ReporteFacturasPage";
import ReportePagosPage from "./pages/ReportePagosPage";
import OrdenesCompraListaPage from "./pages/OrdenesCompraListaPage";
import OrdenesCompraFormPage from "./pages/OrdenesCompraFormPage";
import OrdenCompraPrintPage from "./pages/OrdenCompraPrintPage";
import NomEmpleadosPage from "./pages/NomEmpleadosPage";
import NomConceptosPage from "./pages/NomConceptosPage";
import NomCatalogosPage from "./pages/NomCatalogosPage";
import NomPeriodosPage from "./pages/NomPeriodosPage";
import NomCalculoPage from "./pages/NomCalculoPage";
import NomReciboPrintPage from "./pages/NomReciboPrintPage";
import OrdenesServicioListaPage from "./pages/OrdenesServicioListaPage";
import OrdenesServicioFormPage from "./pages/OrdenesServicioFormPage";
import OrdenesServicioFirmaPage from "./pages/OrdenesServicioFirmaPage";
import OrdenesServicioPrintPage from "./pages/OrdenesServicioPrintPage";

import {
  initAuthFromStorage,
  logout as doLogout,
} from "./services/authService";

// Redirige al primer módulo al que el usuario tiene acceso
function SmartRedirect() {
  try {
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    const u = raw?.user ?? raw ?? {};
    const can = (p) => p === undefined || p === null ? true : Boolean(p);

    if (can(u?.accesoDashboard))    return <Navigate to="dashboard"             replace />;
    if (can(u?.accesoCotizaciones)) return <Navigate to="cotizaciones"           replace />;
    if (can(u?.accesoFacturas))     return <Navigate to="facturacion/factura40"  replace />;
    if (can(u?.accesoPagos))        return <Navigate to="pagos/clientes"         replace />;
    if (can(u?.accesoProveedores))  return <Navigate to="proveedores/catalogo"   replace />;
    if (can(u?.accesoReportes))     return <Navigate to="reportes/cartera"       replace />;
    if (can(u?.accesoUsuarios))     return <Navigate to="usuarios"               replace />;
    if (can(u?.accesoClientes))     return <Navigate to="clientes"               replace />;
    if (can(u?.accesoProductos))    return <Navigate to="productos"              replace />;
    return                                 <Navigate to="login"                  replace />;
  } catch {
    return <Navigate to="dashboard" replace />;
  }
}

// --- Layout protegido ---
function ProtectedLayout({ isLoggedIn, onLogout }) {
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return (
    <>
      <Menu onLogout={onLogout} />
      <main style={{ padding: "24px 24px 24px 24px", marginTop: 56, minHeight: "calc(100vh - 56px)", background: "var(--clr-bg)" }}>
        <Outlet />
      </main>
    </>
  );
}

// Ruta utilitaria para /logout (opcional)
function LogoutRoute({ onAfter }) {
  const navigate = useNavigate();
  useEffect(() => {
    doLogout();
    onAfter?.();
    navigate("/login", { replace: true });
  }, [navigate, onAfter]);
  return null;
}

function FacturaDetalleModal() {
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(false);

  const closeModal = React.useCallback(() => {
    setVisible(false);
    setTimeout(() => navigate(-1), 200);
  }, [navigate]);

  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // 🎞️ disparar animación de entrada
    setTimeout(() => setVisible(true), 10);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeModal]); // 👈 ESLint feliz

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        opacity: visible ? 1 : 0,
        transition: "opacity 200ms ease",
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "90%",
          maxWidth: 1200,
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 8,
          padding: 16,
          position: "relative",
          transform: visible ? "scale(1)" : "scale(0.95)",
          opacity: visible ? 1 : 0,
          transition: "all 200ms ease",
        }}
      >
        <button
          onClick={closeModal}
          style={{
            position: "absolute",
            top: 8,
            right: 12,
            border: "none",
            background: "none",
            fontSize: 20,
            cursor: "pointer",
          }}
        >
          ✕
        </button>

        <FacturaDetallePage />
      </div>
    </div>
  );
}

function FacturaDetalle40Modal() {
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(false);

  const closeModal = React.useCallback(() => {
    setVisible(false);
    setTimeout(() => navigate(-1), 200);
  }, [navigate]);

  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    setTimeout(() => setVisible(true), 10);

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeModal]);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        opacity: visible ? 1 : 0,
        transition: "opacity 200ms ease",
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "95%",
          maxWidth: 1400,
          maxHeight: "95vh",
          overflowY: "auto",
          borderRadius: 8,
          padding: 20,
          position: "relative",
          transform: visible ? "scale(1)" : "scale(0.95)",
          opacity: visible ? 1 : 0,
          transition: "all 200ms ease",
        }}
      >
        <button
          onClick={closeModal}
          style={{
            position: "absolute",
            top: 10,
            right: 16,
            border: "none",
            background: "none",
            fontSize: 22,
            cursor: "pointer",
          }}
        >
          ✕
        </button>

        <FacturaDetalle40 />
      </div>
    </div>
  );
}

function AppRoutes({ isLoggedIn, handleLogout, handleLoginSuccess }) {
  const location = useLocation();

  // Si vienes desde Cartera con state.background, aquí se guarda
  const background = location.state?.background;

  return (
    <>
      {/* RUTAS NORMALES */}
      <Routes location={background || location}>
        {/* LOGIN */}
        <Route
          path="/login"
          element={
            isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} />
            )
          }
        />

        <Route
          path="/logout"
          element={<LogoutRoute onAfter={handleLogout} />}
        />

        {/* PROTEGIDAS */}
        <Route
          element={
            <ProtectedLayout isLoggedIn={isLoggedIn} onLogout={handleLogout} />
          }
        >
          <Route index element={<SmartRedirect />} />
          <Route path="dashboard" element={<DashboardPage />} />

          <Route path="clientes" element={<ClientesPage />} />
          <Route path="productos" element={<ProductosPage />} />
          <Route path="cotizaciones" element={<CotizacionesPage />} />

          <Route
            path="proveedores/catalogo"
            element={<ProveedoresCatalogo />}
          />
          <Route
            path="proveedores/facturas"
            element={<ProveedoresFacturasPage />}
          />
          <Route path="proveedores/ordenes-compra"              element={<OrdenesCompraListaPage />} />
          <Route path="proveedores/ordenes-compra/nueva"      element={<OrdenesCompraFormPage />} />
          <Route path="proveedores/ordenes-compra/:id/editar" element={<OrdenesCompraFormPage />} />
          <Route path="proveedores/ordenes-compra/:id/print"  element={<OrdenCompraPrintPage />} />

          <Route path="cotizaciones/nueva" element={<CotizacionForm />} />
          <Route
            path="cotizaciones/:id/editar"
            element={<CotizacionEditPage />}
          />

          {/* PAGOS */}
          <Route path="pagos/clientes" element={<PagosClientesPage />} />
          <Route path="pagos/lista" element={<PagosListaPage />} />
          <Route path="pagos/cp/print/:folio/:empresa" element={<CpPrintPage />} />

          {/* REPORTES */}
          <Route path="reportes/cartera"   element={<CarteraPage />} />
          <Route path="reportes/facturas"  element={<ReporteFacturasPage />} />
          <Route path="reportes/pagos"     element={<ReportePagosPage />} />

          {/* NÓMINA */}
          <Route path="nomina/empleados" element={<NomEmpleadosPage />} />
          <Route path="nomina/conceptos" element={<NomConceptosPage />} />
          <Route path="nomina/catalogos" element={<NomCatalogosPage />} />
          <Route path="nomina/periodos" element={<NomPeriodosPage />} />
          <Route path="nomina/periodos/:id/calculo" element={<NomCalculoPage />} />
          <Route path="nomina/recibo/:id" element={<NomReciboPrintPage />} />

          {/* SERVICIOS */}
          <Route path="servicios/ordenes"                     element={<OrdenesServicioListaPage />} />
          <Route path="servicios/ordenes/nueva"               element={<OrdenesServicioFormPage />} />
          <Route path="servicios/ordenes/:id/editar"          element={<OrdenesServicioFormPage />} />
          <Route path="servicios/ordenes/:id/firma"           element={<OrdenesServicioFirmaPage />} />
          <Route path="servicios/ordenes/:id/print"           element={<OrdenesServicioPrintPage />} />

          <Route path="usuarios" element={<UsuariosPage />} />
          <Route path="empresas" element={<EmpresasPage />} />

          {/* FACTURACIÓN NORMAL */}
          <Route path="facturacion/factura40" element={<FacturaPage40 />} />
          <Route path="facturacion/nueva" element={<FacturaFormPage />} />
          <Route path="facturacion/editar/:serie/:factura/:empresa" element={<FacturaFormPage />} />
          <Route
            path="facturacion/detalle/:serie/:factura/:empresa"
            element={<FacturaDetalle40 />}
          />
          <Route
            path="facturacion/print/:serie/:factura/:empresa"
            element={<FacturaPrint40 />}
          />
          <Route
            path="cotizaciones/print/:id"
            element={<CotizacionPrint40 />}
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>

      {/* ========================= */}
      {/* RUTA MODAL DE FACTURA */}
      {/* ========================= */}
      {background && (
        <Routes>
          {/* Modal Cartera */}
          <Route
            path="/factura/detalle/:serie/:factura/:empresa"
            element={<FacturaDetalleModal />}
          />

          {/* Modal Facturación 4.0 */}
          <Route
            path="/facturacion/detalle/:serie/:factura/:empresa"
            element={<FacturaDetalle40Modal />}
          />
        </Routes>
      )}
    </>
  );
}

function App() {
  const readIsLogged = () => !!localStorage.getItem("authToken");

  const [isLoggedIn, setIsLoggedIn] = useState(readIsLogged);

  useEffect(() => {
    initAuthFromStorage();
    setIsLoggedIn(readIsLogged());
  }, []);

  const handleLogout = useMemo(
    () => () => {
      doLogout();
      localStorage.removeItem("auth");
      setIsLoggedIn(false);
    },
    [],
  );

  const handleLoginSuccess = useMemo(
    () => () => {
      setIsLoggedIn(true);
    },
    [],
  );

  return (
    <Router>
      <AppRoutes isLoggedIn={isLoggedIn} handleLogout={handleLogout} handleLoginSuccess={handleLoginSuccess} />
    </Router>
  );
}

export default App;
